import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import AmPmToggle from './AmPmToggle';
import './AmPmTimeInput.css';

/**
 * F2 — AM/PM time input composition.
 *
 * Props:
 *   - value: 24-hour 'HH:MM' string (e.g. '09:00', '21:30')
 *   - onChange: (nextTime: 'HH:MM') => void
 *   - disabled
 *   - ariaLabel
 *   - leadingIcon: when true, renders a small clock icon inside the readout
 *
 * Displays a JetBrains-Mono 12-hour readout next to the vertical AM/PM
 * toggle. In edit mode, hour and minute are edited separately.
 */
export const parseTimeValue = (value) => {
  if (!value || typeof value !== 'string') {
    return { hour12: '', minute: '', meridiem: 'AM' };
  }
  const [rawHour, rawMinute] = value.split(':');
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return { hour12: '', minute: '', meridiem: 'AM' };
  }
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  return {
    hour12: String(hour12).padStart(2, '0'),
    minute: String(minute).padStart(2, '0'),
    meridiem,
  };
};

const normalizeHourInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 2);
  if (!digits) {
    return '';
  }
  const hour = Number(digits);
  if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
    return '';
  }
  return String(hour).padStart(2, '0');
};

const normalizeMinuteInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 2);
  if (!digits) {
    return '';
  }
  const minute = Number(digits);
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) {
    return '';
  }
  return String(minute).padStart(2, '0');
};

const toTwentyFourHourValue = (hourValue, minuteValue, meridiem) => {
  const normalizedHour = normalizeHourInput(hourValue);
  const normalizedMinute = normalizeMinuteInput(minuteValue);
  if (!normalizedHour || !normalizedMinute) {
    return '';
  }
  let hour = Number(normalizedHour);
  if (meridiem === 'PM' && hour < 12) {
    hour += 12;
  }
  if (meridiem === 'AM' && hour === 12) {
    hour = 0;
  }
  return `${String(hour).padStart(2, '0')}:${normalizedMinute}`;
};

const AmPmTimeInput = ({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Time',
  leadingIcon = false,
}) => {
  const { hour12, minute, meridiem } = parseTimeValue(value);
  const display = hour12 && minute ? `${hour12}:${minute}` : '--:--';
  const [draftHour, setDraftHour] = useState(hour12 || '');
  const [draftMinute, setDraftMinute] = useState(minute || '');

  useEffect(() => {
    setDraftHour(hour12 || '');
    setDraftMinute(minute || '');
  }, [hour12, minute]);

  const emitChange = (nextHour, nextMinute, nextMeridiem = meridiem) => {
    const nextValue = toTwentyFourHourValue(nextHour, nextMinute, nextMeridiem);
    if (nextValue) {
      onChange?.(nextValue);
    }
  };

  const handleMeridiemChange = (next) => {
    if (disabled || !onChange) return;
    emitChange(draftHour || hour12, draftMinute || minute, next);
  };

  const handleHourChange = (event) => {
    if (disabled || !onChange) return;
    const nextHour = event.target.value.replace(/\D/g, '').slice(0, 2);
    setDraftHour(nextHour);
    if (nextHour.length === 2) {
      emitChange(nextHour, draftMinute, meridiem);
    }
  };

  const handleMinuteChange = (event) => {
    if (disabled || !onChange) return;
    const nextMinute = event.target.value.replace(/\D/g, '').slice(0, 2);
    setDraftMinute(nextMinute);
    if (nextMinute.length === 2) {
      emitChange(draftHour, nextMinute, meridiem);
    }
  };

  const handleHourBlur = () => {
    const normalizedHour = normalizeHourInput(draftHour) || hour12 || '';
    const normalizedMinute = normalizeMinuteInput(draftMinute) || minute || '';
    setDraftHour(normalizedHour);
    setDraftMinute(normalizedMinute);
    emitChange(normalizedHour, normalizedMinute, meridiem);
  };

  const handleMinuteBlur = () => {
    const normalizedHour = normalizeHourInput(draftHour) || hour12 || '';
    const normalizedMinute = normalizeMinuteInput(draftMinute) || minute || '';
    setDraftHour(normalizedHour);
    setDraftMinute(normalizedMinute);
    emitChange(normalizedHour, normalizedMinute, meridiem);
  };

  return (
    <div
      className={`appointments-v3-ampm-time ${disabled ? 'is-disabled' : ''}`}
      role="group"
      aria-label={ariaLabel}
      title={disabled ? 'Select Edit to update' : undefined}
    >
      <span className="appointments-v3-ampm-time-display">
        {leadingIcon && (
          <Clock
            size={14}
            strokeWidth={2}
            className="appointments-v3-ampm-time-icon"
            aria-hidden="true"
          />
        )}
        {disabled ? (
          <span
            className="appointments-v3-ampm-time-value"
            aria-label={`${ariaLabel}: ${display} ${meridiem}`}
          >
            {display}
          </span>
        ) : (
          <span className="appointments-v3-ampm-time-fields">
            <input
              type="text"
              className="appointments-v3-ampm-time-input"
              value={draftHour}
              onChange={handleHourChange}
              onBlur={handleHourBlur}
              aria-label={`${ariaLabel} hour`}
              inputMode="numeric"
              placeholder="hh"
              maxLength={2}
            />
            <span className="appointments-v3-ampm-time-colon" aria-hidden="true">:</span>
            <input
              type="text"
              className="appointments-v3-ampm-time-input"
              value={draftMinute}
              onChange={handleMinuteChange}
              onBlur={handleMinuteBlur}
              aria-label={`${ariaLabel} minute`}
              inputMode="numeric"
              placeholder="mm"
              maxLength={2}
            />
          </span>
        )}
      </span>
      <AmPmToggle
        value={meridiem}
        onChange={handleMeridiemChange}
        disabled={disabled}
        ariaLabel={`${ariaLabel} meridiem`}
      />
    </div>
  );
};

export default AmPmTimeInput;
